// VRM avatar format: detection, upload limits and license rules.
//
// Deliberately free of three / @pixiv/three-vrm imports so any screen can ask
// "is this a VRM?" without pulling the VRM loader into the main bundle. The
// loader itself lives in ./loadVrm.ts and is imported lazily.

/**
 * Largest .vrm we store (also enforced by functions/api/storage/_rules.ts).
 * Picked files are optimised in the browser first (./optimizeVrm.ts), so this
 * is the limit after textures are scaled to 2048 px / 1024 px.
 */
export const VRM_MAX_BYTES = 40 * 1024 * 1024;
/** Largest .vrm the upload dialog will pick up and try to optimise. */
export const VRM_INPUT_MAX_BYTES = 150 * 1024 * 1024;
/** Above this many triangles an avatar is heavy for Quest 3 (warn, don't block). */
export const VRM_WARN_TRIANGLES = 70_000;
/** Textures larger than this are downscaled at load on Quest (desktop: 2x). */
export const VRM_QUEST_TEXTURE_CAP = 1024;
export const VRM_DESKTOP_TEXTURE_CAP = 2048;

/** Shared Tripo library clips: VRM humanoids play these, retargeted at load. */
export const VRM_BASE_CLIPS = {
  idle: 'https://assets.gathrxr.com/avatars/animations/tripo-library/idle.glb',
  walking: 'https://assets.gathrxr.com/avatars/animations/tripo-library/walk.glb',
  running: 'https://assets.gathrxr.com/avatars/animations/tripo-library/run.glb',
  jumping: 'https://assets.gathrxr.com/avatars/animations/tripo-library/jump.glb',
} as const;

/** URL ends in .vrm (our uploads are always stored as avatars/vrm/<...>.vrm). */
export function isVrmUrl(url: string | null | undefined): boolean {
  return !!url && /\.vrm(?:[?#]|$)/i.test(url);
}

/**
 * Is this avatar a VRM? Either the model URL says so or the avatar's
 * `animations` JSON carries `__format: 'vrm'` (that JSON is broadcast as
 * animUrls, so remote viewers learn the format with no extra plumbing).
 */
export function isVrmAvatar(url: string | null | undefined, anims?: Record<string, unknown> | null): boolean {
  return isVrmUrl(url) || String(anims?.__format ?? '').toLowerCase() === 'vrm';
}

// ---- License ----

export type VrmAvatarPermission = 'everyone' | 'onlyAuthor' | 'onlySeparatelyLicensedPerson';

/** The license fields we act on, normalised across VRM 0.x and 1.0 metadata. */
export interface VrmLicense {
  specVersion: '0' | '1';
  title: string;
  authors: string[];
  version: string;
  /** Who may wear ("perform as") this avatar. */
  avatarPermission: VrmAvatarPermission;
  /** May a copy of the file be handed to someone else (sell / gift)? */
  allowRedistribution: boolean;
  /** Commercial use allowed (selling in the marketplace counts). */
  commercial: boolean;
  /** Human-readable license line, e.g. "CC0", "VRM Public License 1.0". */
  licenseName: string;
  licenseUrl: string | null;
  otherPermissionUrl: string | null;
  allowViolent: boolean;
  allowSexual: boolean;
  creditNotation: 'required' | 'unnecessary' | null;
  copyright: string | null;
}

// VRM 0.x licenseName values that forbid passing the file on.
const V0_NO_REDISTRIBUTION = new Set(['Redistribution_Prohibited']);
const V0_LICENSE_LABEL: Record<string, string> = {
  Redistribution_Prohibited: 'Redistribution prohibited',
  CC0: 'CC0', CC_BY: 'CC BY', CC_BY_NC: 'CC BY-NC', CC_BY_SA: 'CC BY-SA', CC_BY_NC_SA: 'CC BY-NC-SA',
  CC_BY_ND: 'CC BY-ND', CC_BY_NC_ND: 'CC BY-NC-ND', Other: 'Other (see author terms)',
};

const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

/** Normalise three-vrm's `vrm.meta` (VRM0Meta or VRM1Meta) into the fields we enforce. */
export function readVrmLicense(meta: any): VrmLicense {
  if (meta?.metaVersion === '0') {
    const allowed = str(meta.allowedUserName);
    const lic = str(meta.licenseName);
    return {
      specVersion: '0',
      title: str(meta.title) || 'VRM avatar',
      authors: str(meta.author) ? [str(meta.author)] : [],
      version: str(meta.version),
      avatarPermission: allowed === 'OnlyAuthor' ? 'onlyAuthor' : allowed === 'ExplicitlyLicensedPerson' ? 'onlySeparatelyLicensedPerson' : 'everyone',
      // Only Redistribution_Prohibited forbids copies (ND licenses allow unmodified ones).
      allowRedistribution: !V0_NO_REDISTRIBUTION.has(lic),
      commercial: str(meta.commercialUssageName) !== 'Disallow' && !/_NC/.test(lic),
      licenseName: V0_LICENSE_LABEL[lic] || lic || 'Not specified',
      licenseUrl: str(meta.otherLicenseUrl) || null,
      otherPermissionUrl: str(meta.otherPermissionUrl) || null,
      allowViolent: str(meta.violentUssageName) !== 'Disallow',
      allowSexual: str(meta.sexualUssageName) !== 'Disallow',
      creditNotation: /^CC_BY/.test(lic) ? 'required' : null,
      copyright: null,
    };
  }
  const perm = str(meta?.avatarPermission);
  return {
    specVersion: '1',
    title: str(meta?.name) || 'VRM avatar',
    authors: Array.isArray(meta?.authors) ? meta.authors.map(str).filter(Boolean) : [],
    version: str(meta?.version),
    avatarPermission: perm === 'onlyAuthor' || perm === 'onlySeparatelyLicensedPerson' ? perm : 'everyone',
    // VRM 1.0 spec: allowRedistribution defaults to false when absent.
    allowRedistribution: meta?.allowRedistribution === true,
    commercial: str(meta?.commercialUsage) === 'personalProfit' || str(meta?.commercialUsage) === 'corporation',
    licenseName: /vrm\.dev\/licenses\/1\.0/.test(str(meta?.licenseUrl)) ? 'VRM Public License 1.0' : str(meta?.licenseUrl) ? 'Custom license' : 'Not specified',
    licenseUrl: str(meta?.licenseUrl) || null,
    otherPermissionUrl: str(meta?.otherLicenseUrl) || null,
    allowViolent: meta?.allowExcessivelyViolentUsage === true,
    allowSexual: meta?.allowExcessivelySexualUsage === true,
    creditNotation: str(meta?.creditNotation) === 'required' ? 'required' : str(meta?.creditNotation) === 'unnecessary' ? 'unnecessary' : null,
    copyright: str(meta?.copyrightInformation) || null,
  };
}

/**
 * What the license lets this uploader do. Wearing a restricted avatar needs the
 * uploader to state they are its author / hold a separate license; anything
 * the file forbids (handing copies on) is blocked outright.
 */
export function vrmUsePolicy(lic: VrmLicense): {
  /** Text of the statement the uploader must confirm before wearing, or null. */
  confirmToWear: string | null;
  canSell: boolean;
  canGift: boolean;
  notes: string[];
} {
  const notes: string[] = [];
  let confirmToWear: string | null = null;
  if (lic.avatarPermission === 'onlyAuthor') {
    confirmToWear = 'I am the author of this avatar. Its license only lets the author use it as an avatar.';
  } else if (lic.avatarPermission === 'onlySeparatelyLicensedPerson') {
    confirmToWear = 'I have a separate license from the author that lets me use this avatar.';
  }
  if (!lic.allowRedistribution) notes.push('Redistribution is not allowed, so it can’t be sold or gifted.');
  if (!lic.commercial) notes.push('Commercial use is not allowed, so it can’t be sold.');
  if (lic.creditNotation === 'required') notes.push(`Credit required: ${lic.authors.join(', ') || 'the author'}.`);
  return {
    confirmToWear,
    canSell: lic.allowRedistribution && lic.commercial,
    canGift: lic.allowRedistribution,
    notes,
  };
}

/** creation_params entries (strings only — the column is Record<string,string>). */
export function vrmCreationParams(lic: VrmLicense, stats: { triangles: number; heavy: boolean }): Record<string, string> {
  return {
    source: 'vrm',
    vrm_version: lic.specVersion,
    vrm_title: lic.title.slice(0, 120),
    vrm_authors: lic.authors.join(', ').slice(0, 200),
    vrm_avatar_permission: lic.avatarPermission,
    vrm_redistribution: String(lic.allowRedistribution),
    vrm_commercial: String(lic.commercial),
    vrm_license: lic.licenseName.slice(0, 120),
    ...(lic.licenseUrl ? { vrm_license_url: lic.licenseUrl.slice(0, 300) } : {}),
    vrm_triangles: String(stats.triangles),
    ...(stats.heavy ? { vrm_heavy: 'true' } : {}),
  };
}

/** Wardrobe: is this saved avatar a VRM whose license forbids passing it on? */
export function vrmBlocksTransfer(creationParams: Record<string, string> | null | undefined): { sell: boolean; gift: boolean } {
  if (creationParams?.source !== 'vrm') return { sell: false, gift: false };
  const redistribute = creationParams.vrm_redistribution === 'true';
  return { sell: !redistribute || creationParams.vrm_commercial !== 'true', gift: !redistribute };
}
